import React, { useState, useEffect } from 'react';
import { useTranslations } from 'react-ui';
import { useFormStore } from 'simple-creator/stores/formStore';
import { EventLocationForm } from '../inputs/EventLocationForm';
import { useAutoResolveLocation } from '../../hooks/useAutoResolveLocation';

export const WhenWhereStep: React.FC = () => {
  const { formData, updateField } = useFormStore();
  const { translated } = useTranslations();
  const { isResolving, resolveError } = useAutoResolveLocation();
  const [dateInput, setDateInput] = useState('');
  const [timeInput, setTimeInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [endTimeInput, setEndTimeInput] = useState('');
  const [showEndTime, setShowEndTime] = useState(false);
  const [additionalDates, setAdditionalDates] = useState<Array<{date: string, time: string, endDate: string | null, endTime: string | null, showEndTime: boolean}>>([]); 

  // Initialize date inputs from formData if exists
  useEffect(() => {
    if (formData.dates.length > 0) {
      const firstDate = new Date(formData.dates[0].start);
      const firstEnd = formData.dates[0].end ? new Date(formData.dates[0].end) : null;
      console.log('Initializing date inputs from formData:', { firstDate, firstEnd });
      setDateInput(firstDate.toISOString().split('T')[0]);
      setTimeInput(firstDate.toTimeString().slice(0, 5));

      // Restore end date/time only if user explicitly set it.
      // When no end is set, end === start in formData, so we detect
      // an explicit end by checking if end differs from start.
      const hasExplicitEnd = firstEnd && firstEnd.getTime() !== firstDate.getTime();
      if (hasExplicitEnd) {
        setEndDateInput(firstEnd.toISOString().split('T')[0]);
        setEndTimeInput(firstEnd.toTimeString().slice(0, 5));
        setShowEndTime(true);
      }
      
      // Load additional dates
      if (formData.dates.length > 1) {
        const additionals = formData.dates.slice(1).map(d => {
          const start = new Date(d.start);
          const end = d.end ? new Date(d.end) : null;
          const explicit = end && end.getTime() !== start.getTime();
          return {
            date: start.toISOString().split('T')[0],
            time: start.toTimeString().slice(0, 5),
            endDate: explicit ? end.toISOString().split('T')[0] : null,
            endTime: explicit ? end.toTimeString().slice(0, 5) : null,
            showEndTime: explicit || false,
          };
        });
        setAdditionalDates(additionals);
      }
    }
  }, []);

  // Auto-update dates when inputs change
  useEffect(() => {
    if (!dateInput || !timeInput) {
      if (formData.dates.length > 0 && !dateInput && !timeInput) {
        updateField('dates', []);
      }
      return;
    }

    const startDate = new Date(`${dateInput}T${timeInput}`);
    // When no end date is explicitly set, end = start (signals "no end set")
    const endDate = endDateInput && endTimeInput
      ? new Date(`${endDateInput}T${endTimeInput}`)
      : null;

    const newDate = {
      start: startDate,
      end: endDate,
    };

    const additionalDateObjects = additionalDates
      .filter(d => d.date && d.time)
      .map(d => {
        const s = new Date(`${d.date}T${d.time}`);
        const e = d.endDate && d.endTime
          ? new Date(`${d.endDate}T${d.endTime}`)
          : null;
        return { start: s, end: e };
      });
    
    const newDates = [newDate, ...additionalDateObjects];
    
    // Only update if dates actually changed (compare by serialization)
    const currentDatesKey = JSON.stringify(formData.dates.map(d => ({ 
      start: d.start.getTime(), 
      end: d.end ? d.end.getTime() : null 
    })));
    const newDatesKey = JSON.stringify(newDates.map(d => ({ 
      start: d.start.getTime(), 
      end: d.end ? d.end.getTime() : null 
    })));
    
    if (currentDatesKey !== newDatesKey) {
      updateField('dates', newDates);
    }
  }, [dateInput, timeInput, endDateInput, endTimeInput, additionalDates, formData.dates, updateField]);

  const handleClearFirstDate = () => {
    setDateInput('');
    setTimeInput('');
    setEndDateInput('');
    setEndTimeInput('');
    setShowEndTime(false);
    setAdditionalDates([]);
    updateField('dates', []);
  };

  const handleAddAnotherDate = () => {
    setAdditionalDates([...additionalDates, { date: '', time: '', endDate: '', endTime: '', showEndTime: false }]);
  };

  const handleRemoveAdditionalDate = (index: number) => {
    const newAdditional = additionalDates.filter((_, i) => i !== index);
    setAdditionalDates(newAdditional);
  };

  const handleUpdateAdditionalDate = (index: number, field: 'date' | 'time' | 'endDate' | 'endTime', value: string) => {
    const updated = [...additionalDates];
    updated[index] = { ...updated[index], [field]: value };
    // If the user updates end date or time, ensure it is not before the start date/time
    if ((field === 'endDate' || field === 'endTime') && updated[index].date && updated[index].time && updated[index].endDate && updated[index].endTime) {
      const start = new Date(`${updated[index].date}T${updated[index].time}`);
      const end = new Date(`${updated[index].endDate}T${updated[index].endTime}`);
      if (end < start) {
        // Reset end to match start to maintain invariant
        updated[index].endDate = updated[index].date;
        updated[index].endTime = updated[index].time;
      }
    }
    setAdditionalDates(updated);
  };

  const handleToggleAdditionalEndTime = (index: number, show: boolean) => {
    const updated = [...additionalDates];
    updated[index] = { ...updated[index], showEndTime: show, endDate: show ? updated[index].endDate : '', endTime: show ? updated[index].endTime : '' };
    setAdditionalDates(updated);
  };

  const handleLocationChange = (location: typeof formData.location) => {
    updateField('location', location);
  };

  const handleAddPrimaryEndTime = () => {
    const s = new Date(`${dateInput}T${timeInput}`);
    const e = new Date(s.getTime() + 2 * 60 * 60 * 1000);
    setEndDateInput(e.toISOString().split('T')[0]);
    setEndTimeInput(e.toTimeString().slice(0, 5));
    setShowEndTime(true);
  };

  const handleRemovePrimaryEndTime = () => {
    setEndDateInput('');
    setEndTimeInput('');
    setShowEndTime(false);
  };

  const handleQuickToday = () => {
    const today = new Date();
    setDateInput(today.toISOString().split('T')[0]);
    setTimeInput('19:00');
  };

  const handleQuickTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDateInput(tomorrow.toISOString().split('T')[0]);
    setTimeInput('19:00');
  };

  const handleQuickWeekend = () => {
    const weekend = new Date();
    const day = weekend.getDay();
    const daysUntilSaturday = (6 - day + 7) % 7 || 7;
    weekend.setDate(weekend.getDate() + daysUntilSaturday);
    setDateInput(weekend.toISOString().split('T')[0]);
    setTimeInput('20:00');
  };

  // Ensure end date/time is never before start date/time for the primary date
  useEffect(() => {
    if (!dateInput || !timeInput) return;
    if (endDateInput && endTimeInput) {
      const start = new Date(`${dateInput}T${timeInput}`);
      const end = new Date(`${endDateInput}T${endTimeInput}`);
      if (end < start) {
        // Reset end to start values
        setEndDateInput(dateInput);
        setEndTimeInput(timeInput);
      }
    }
  }, [dateInput, timeInput, endDateInput, endTimeInput]);

  const handleAddAdditionalEndTime = (index: number, additionalDate: typeof additionalDates[number]) => {
    const s = new Date(`${additionalDate.date}T${additionalDate.time}`);
    const e = new Date(s.getTime() + 2 * 60 * 60 * 1000);
    handleUpdateAdditionalDate(index, 'endDate', e.toISOString().split('T')[0]);
    handleUpdateAdditionalDate(index, 'endTime', e.toTimeString().slice(0, 5));
    handleToggleAdditionalEndTime(index, true);
  };

  const handleQuickTodayAdditional = (index: number) => {
    const today = new Date();
    handleUpdateAdditionalDate(index, 'date', today.toISOString().split('T')[0]);
    handleUpdateAdditionalDate(index, 'time', '19:00');
  };

  const handleQuickTomorrowAdditional = (index: number) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    handleUpdateAdditionalDate(index, 'date', tomorrow.toISOString().split('T')[0]);
    handleUpdateAdditionalDate(index, 'time', '19:00');
  };

  const handleQuickWeekendAdditional = (index: number) => {
    const weekend = new Date();
    const day = weekend.getDay();
    const daysUntilSaturday = (6 - day + 7) % 7 || 7;
    weekend.setDate(weekend.getDate() + daysUntilSaturday);
    handleUpdateAdditionalDate(index, 'date', weekend.toISOString().split('T')[0]);
    handleUpdateAdditionalDate(index, 'time', '20:00');
  };

  const handleClearLocation = () => {
    handleLocationChange({ name: '', address: '', coordinates: { lat: 0, lng: 0 } });
  };

  return (
    <div className="space-y-6">
      {/* Date & Time */}
      <div>
        <label className="label block mb-2">
          When is it happening? <span className="text-red-500">*</span>
        </label>
        
        {/* First date/time (always visible) */}
        <div className="flex items-center gap-2 mb-2">
          {showEndTime && <span className="text-sm text-gray-500 shrink-0">Start:</span>}
          <input
            type="date"
            value={dateInput}
            onChange={(e) => setDateInput(e.target.value)}
            className="input flex-1"
            min={new Date().toISOString().split('T')[0]}
          />
          <input
            type="time"
            value={timeInput}
            onChange={(e) => setTimeInput(e.target.value)}
            className="input flex-1"
          />
          {dateInput && timeInput && (
            <button
              type="button"
              onClick={handleClearFirstDate}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm"
              title="Clear date"
            >
              ✕
            </button>
          )}
        </div>

        {/* End date/time for primary date */}
        {dateInput && timeInput && !showEndTime && (
          <button
            type="button"
            onClick={handleAddPrimaryEndTime}
            className="text-sm text-balloon hover:underline cursor-pointer mb-2"
          >
            + End date & time
          </button>
        )}
        {dateInput && timeInput && showEndTime && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-500 shrink-0">End:</span>
            <input
              type="date"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              className="input flex-1"
              min={dateInput}
            />
            <input
              type="time"
              value={endTimeInput}
              onChange={(e) => setEndTimeInput(e.target.value)}
              className="input flex-1"
            />
            <button
              type="button"
              onClick={handleRemovePrimaryEndTime}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm"
              title="Remove end date/time"
            >
              ✕
            </button>
          </div>
        )}

        {dateInput && timeInput && (
          <p className="text-xs text-green-600 mb-4 flex items-center gap-1">
            <span>✓</span>
            <span>Date automatically saved</span>
          </p>
        )}

        {/* Quick options */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={handleQuickToday}
            className="px-3 py-1 bg-gray-100 rounded-full text-sm"
          >
            Today
          </button>
          <button
            type="button"
            onClick={handleQuickTomorrow}
            className="px-3 py-1 bg-gray-100 rounded-full text-sm"
          >
            Tomorrow
          </button>
          <button
            type="button"
            onClick={handleQuickWeekend}
            className="px-3 py-1 bg-gray-100 rounded-full text-sm"
          >
            This Weekend
          </button>
        </div>

        {/* Additional date fields */}
        {additionalDates.map((additionalDate, index) => (
          <div key={index} className="pl-4 border-l-2 border-gray-200 mb-4">
            <div className="flex gap-2 mb-2">
              <input
                type="date"
                value={additionalDate.date}
                onChange={(e) => handleUpdateAdditionalDate(index, 'date', e.target.value)}
                className="input flex-1"
                min={new Date().toISOString().split('T')[0]}
              />
              <input
                type="time"
                value={additionalDate.time}
                onChange={(e) => handleUpdateAdditionalDate(index, 'time', e.target.value)}
                className="input flex-1"
              />
              <button
                type="button"
                onClick={() => handleRemoveAdditionalDate(index)}
                className="px-4 py-2 text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove this date"
              >
                ✕
              </button>
            </div>

            {/* End date/time for additional date */}
            {additionalDate.date && additionalDate.time && !additionalDate.showEndTime && (
              <button
                type="button"
                onClick={() => handleAddAdditionalEndTime(index, additionalDate)}
                className="text-xs text-balloon hover:underline cursor-pointer mb-2"
              >
                + End date & time
              </button>
            )}
            {additionalDate.showEndTime && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500 shrink-0">End:</span>
                <input
                  type="date"
                  value={additionalDate.endDate || ''}
                  onChange={(e) => handleUpdateAdditionalDate(index, 'endDate', e.target.value)}
                  className="input flex-1 text-sm"
                  min={additionalDate.date}
                />
                <input
                  type="time"
                  value={additionalDate.endTime || ''}
                  onChange={(e) => handleUpdateAdditionalDate(index, 'endTime', e.target.value)}
                  className="input flex-1 text-sm"
                />
                <button
                  type="button"
                  onClick={() => handleToggleAdditionalEndTime(index, false)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm"
                  title="Remove end date/time"
                >
                  ✕
                </button>
              </div>
            )}
            
            {/* Quick options for additional date */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => handleQuickTodayAdditional(index)}
                className="px-2 py-1 bg-gray-100 rounded-full text-xs"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => handleQuickTomorrowAdditional(index)}
                className="px-2 py-1 bg-gray-100 rounded-full text-xs"
              >
                Tomorrow
              </button>
              <button
                type="button"
                onClick={() => handleQuickWeekendAdditional(index)}
                className="px-2 py-1 bg-gray-100 rounded-full text-xs"
              >
                This Weekend
              </button>
            </div>
          </div>
        ))}

        {/* Add another date button */}
        {dateInput && timeInput && (
          <button
            type="button"
            onClick={handleAddAnotherDate}
            className="w-full py-3 mt-2 text-balloon border-2 border-dashed border-balloon hover:bg-balloon hover:text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
          >
            <span className="text-xl">+</span>
            <span>Add another date...</span>
          </button>
        )}
      </div>

      {/* Location */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label">
            Where is it? <span className="text-red-500">*</span>
          </label>
          {(formData.location.address || formData.location.name || formData.location.coordinates.lat !== 0) && (
            <button
              type="button"
              onClick={handleClearLocation}
              className="text-xs text-red-500 hover:text-red-700 hover:underline transition-colors"
            >
              Clear location
            </button>
          )}
        </div>

        <EventLocationForm
          value={formData.location}
          onChange={handleLocationChange}
          showVenueName={true}
          venueNamePlaceholder="Venue name (optional)"
          addressPlaceholder="Start typing an address..."
        />

        <p className="text-xs text-gray-500 mt-2">
          {isResolving ? (
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 border-2 border-balloon border-t-transparent rounded-full animate-spin" />
              {translated('sc_location_resolving')}
            </span>
          ) : formData.location.coordinates.lat && formData.location.coordinates.lng ? (
            <>✅ {translated('sc_location_set_from_map')}</>
          ) : resolveError ? (
            <>⚠️ {translated('sc_location_resolve_failed')}</>
          ) : (
            <>💡 {translated('sc_location_tip')}</>
          )}
        </p>
      </div>

      {/* Preview */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-3">Event Schedule Preview:</p>
        {formData.dates.length > 0 ? (
          <div className="space-y-2">
            {formData.dates.map((date, index) => (
              <div key={index} className="flex items-start gap-2 text-sm bg-white p-2 rounded">
                <span className="text-lg">📅</span>
                <div>
                  <div className="font-medium">
                    {new Date(date.start).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="text-gray-600 text-xs">
                    {new Date(date.start).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}{date.end && ' – '}{date.end && new Date(date.start).toDateString() !== new Date(date.end).toDateString() && (
                      <>{new Date(date.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} </>
                    )}{date.end && new Date(date.end).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
                {index === 0 && formData.dates.length === 1 && (
                  <span className="ml-auto text-xs bg-balloon/10 text-balloon px-2 py-1 rounded-full">Main Event</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Select a date and time above</p>
        )}
        
        {formData.location.address && (
          <div className="flex items-start gap-2 text-sm mt-3 pt-3 border-t border-gray-200">
            <span className="text-lg">📍</span>
            <div>
              {formData.location.name && (
                <div className="font-medium">{formData.location.name}</div>
              )}
              <div className="text-gray-600 text-xs">{formData.location.address}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
